/*
 * MOGRA theme interactions.
 * Clicks, changes and submits use document-level delegation, so they keep
 * working when the theme editor re-renders a section. Slideshows and
 * carousels initialise on load and on `shopify:section:load`.
 */
(function () {
  'use strict';

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function closest(target, selector) {
    return target && target.closest ? target.closest(selector) : null;
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, wait);
    };
  }

  // Clipboard API where allowed (https), else the older select-and-copy route.
  function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(text);
    return new Promise(function (resolve, reject) {
      var field = document.createElement('textarea');
      field.value = text;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      document.body.appendChild(field);
      field.select();
      var ok = false;
      try { ok = document.execCommand('copy'); } catch (e) {}
      document.body.removeChild(field);
      if (ok) resolve(); else reject(new Error('copy failed'));
    });
  }

  function updateCartCount() {
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
        });
        return cart;
      });
  }

  function addToCart(form) {
    var body = new FormData(form);
    // "One-time purchase" is a blank selling plan; leave it out rather than send an empty value.
    if (body.get('selling_plan') === '') body.delete('selling_plan');
    // Ask Shopify to render the refreshed drawer in the same response.
    if (getCartDrawer()) {
      body.append('sections', 'cart-drawer');
      body.append('sections_url', window.location.pathname);
    }
    return fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) throw shopError(data);
        // An empty object means Shopify added nothing.
        if (!data || (!data.id && !data.items)) throw shopError(null);
        if (data.sections) renderCartDrawer(data.sections['cart-drawer']);
        // The item is in the bag now; a failed count refresh must not report the add as failed.
        return updateCartCount().catch(function () {});
      });
    });
  }

  /* ---------- mobile menu drawer ---------- */
  var drawerTrigger = null;

  function openDrawer(drawer, trigger) {
    clearTimeout(drawer._closeTimer);
    drawer.hidden = false;
    // Add the class a frame later so the slide / fade transition starts from the closed state.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { drawer.classList.add('is-open'); });
    });
    document.documentElement.style.overflow = 'hidden';
    drawerTrigger = trigger || null;
    if (trigger && trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'true');
    var first = drawer.querySelector('[data-drawer-focus]') || drawer.querySelector('button, a');
    if (first) first.focus({ preventScroll: true });
  }

  function closeDrawer(drawer) {
    drawer.classList.remove('is-open');
    drawer._closeTimer = setTimeout(function () { drawer.hidden = true; }, reduceMotion ? 0 : 300);
    document.documentElement.style.overflow = '';
    if (drawerTrigger) {
      if (drawerTrigger.hasAttribute('aria-expanded')) drawerTrigger.setAttribute('aria-expanded', 'false');
      drawerTrigger.focus({ preventScroll: true });
      drawerTrigger = null;
    }
  }

  /* ---------- cart drawer ---------- */
  function getCartDrawer() {
    return document.getElementById('CartDrawer');
  }

  function renderCartDrawer(html) {
    var drawer = getCartDrawer();
    if (!drawer || !html) return;
    var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-drawer-content]');
    var current = drawer.querySelector('[data-cart-drawer-content]');
    if (fresh && current) current.replaceWith(fresh);
  }

  // Errors carry Shopify's own wording (e.g. "only 2 left") in shopMessage; network or parse
  // failures don't, so callers fall back to their localized default instead of "Failed to fetch".
  function shopError(data) {
    var error = new Error('cart request failed');
    error.shopMessage = (data && (data.description || data.message)) || '';
    return error;
  }

  var cartChangeBusy = false;

  // Lines are addressed by line item key, not position, so a stale or repeated request
  // can never change a different item.
  function changeCartLine(key, quantity, focusId) {
    if (cartChangeBusy) return;
    cartChangeBusy = true;
    var drawer = getCartDrawer();
    var content = drawer && drawer.querySelector('[data-cart-drawer-content]');
    if (content) content.classList.add('pointer-events-none', 'opacity-60');
    return fetch(root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: Math.max(0, quantity || 0), sections: 'cart-drawer', sections_url: window.location.pathname })
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw shopError(data);
          return data;
        });
      })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function (el) { el.textContent = cart.item_count; });
        renderCartDrawer(cart.sections && cart.sections['cart-drawer']);
        // The swap replaced the focused button; put focus back on its replacement (or the close button).
        if (drawer && !drawer.hidden) {
          var target = (focusId && drawer.querySelector('[data-cart-focus="' + focusId + '"]')) || drawer.querySelector('[data-drawer-focus]');
          if (target) target.focus({ preventScroll: true });
        }
      })
      .catch(function (error) {
        var box = drawer && drawer.querySelector('[data-cart-drawer-error]');
        if (box) {
          box.textContent = error.shopMessage || box.getAttribute('data-default');
          box.hidden = false;
        }
        if (content) content.classList.remove('pointer-events-none', 'opacity-60');
      })
      .then(function () { cartChangeBusy = false; });
  }

  /* ---------- clicks ---------- */
  document.addEventListener('click', function (event) {
    // Bag icons open the drawer (the link still goes to /cart without JS or with a modifier key).
    var cartOpener = closest(event.target, '[data-cart-drawer-open]');
    var cartDrawer = getCartDrawer();
    if (cartOpener && cartDrawer && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
      event.preventDefault();
      openDrawer(cartDrawer, cartOpener);
      return;
    }

    var lineChange = closest(event.target, '[data-cart-line-change]');
    if (lineChange) {
      event.preventDefault();
      changeCartLine(
        lineChange.getAttribute('data-line-key'),
        parseInt(lineChange.getAttribute('data-quantity'), 10),
        lineChange.getAttribute('data-cart-focus')
      );
      return;
    }

    var opener = closest(event.target, '[data-drawer-open]');
    if (opener) {
      var drawer = document.getElementById(opener.getAttribute('aria-controls'));
      if (drawer) openDrawer(drawer, opener);
      return;
    }

    var closer = closest(event.target, '[data-drawer-close]');
    if (closer) {
      var openDrawerEl = closest(closer, '[data-drawer]');
      if (openDrawerEl) closeDrawer(openDrawerEl);
      return;
    }

    var thumb = closest(event.target, '[data-thumb]');
    if (thumb) {
      showMedia(closest(thumb, '[data-product-section]'), thumb.getAttribute('data-media-id'));
      return;
    }

    var galleryStep = closest(event.target, '[data-gallery-prev], [data-gallery-next]');
    if (galleryStep) {
      var gallery = closest(galleryStep, '[data-gallery]');
      scrollGallery(gallery, (gallery._index || 0) + (galleryStep.hasAttribute('data-gallery-next') ? 1 : -1));
      return;
    }

    var step = closest(event.target, '[data-qty-minus], [data-qty-plus]');
    if (step) {
      var input = step.parentElement.querySelector('[data-qty-input]');
      var value = parseInt(input.value, 10) || 1;
      input.value = Math.max(1, value + (step.hasAttribute('data-qty-plus') ? 1 : -1));
      return;
    }

    var videoToggle = closest(event.target, '[data-video-toggle]');
    if (videoToggle) {
      var slideVideo = closest(videoToggle, '[data-slide]').querySelector('video');
      if (slideVideo) {
        if (slideVideo.paused) {
          slideVideo._userPaused = false;
          playVideo(slideVideo);
          setVideoToggle(videoToggle, true);
        } else {
          slideVideo._userPaused = true;
          slideVideo.pause();
          setVideoToggle(videoToggle, false);
        }
      }
      return;
    }

    var slideDot = closest(event.target, '[data-slide-dot]');
    if (slideDot) {
      var slideshow = closest(slideDot, '[data-slideshow]');
      if (slideshow && slideshow._goTo) {
        slideshow._goTo(parseInt(slideDot.getAttribute('data-slide-dot'), 10));
        slideshow._restart();
      }
      return;
    }

    var carouselDot = closest(event.target, '[data-carousel-dot]');
    if (carouselDot) {
      var track = closest(carouselDot, '[data-carousel]').querySelector('[data-carousel-track]');
      track.scrollTo({
        left: parseInt(carouselDot.getAttribute('data-carousel-dot'), 10) * track.clientWidth,
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
      return;
    }

    var carouselStep = closest(event.target, '[data-carousel-prev], [data-carousel-next]');
    if (carouselStep) {
      var stepTrack = closest(carouselStep, '[data-carousel]').querySelector('[data-carousel-track]');
      stepTrack.scrollBy({
        left: (carouselStep.hasAttribute('data-carousel-prev') ? -1 : 1) * stepTrack.clientWidth,
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
      return;
    }

    var copyCode = closest(event.target, '[data-copy-code]');
    if (copyCode) {
      copyText(copyCode.getAttribute('data-copy-code')).then(function () {
        var copyLabel = copyCode.querySelector('[data-copy-label]');
        if (!copyLabel) return;
        clearTimeout(copyCode._copyTimer);
        if (!copyCode._label) copyCode._label = copyLabel.textContent;
        copyLabel.textContent = copyCode.getAttribute('data-copied');
        copyCode._copyTimer = setTimeout(function () { copyLabel.textContent = copyCode._label; }, 2000);
      }).catch(function () {});
      return;
    }

    var share = closest(event.target, '[data-share]');
    if (share) {
      var data = { title: share.getAttribute('data-share-title'), url: share.getAttribute('data-share-url') };
      if (navigator.share) {
        navigator.share(data).catch(function () {});
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(data.url).then(function () {
          var label = share.querySelector('[data-share-label]');
          var original = label.textContent;
          label.textContent = share.getAttribute('data-share-copied');
          setTimeout(function () { label.textContent = original; }, 2000);
        });
      }
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      document.querySelectorAll('[data-drawer]:not([hidden])').forEach(closeDrawer);
      return;
    }
    // Keep Tab inside an open drawer (they are modal dialogs).
    if (event.key !== 'Tab') return;
    var open = document.querySelector('[data-drawer].is-open');
    if (!open) return;
    var focusable = Array.prototype.filter.call(
      open.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'),
      function (el) { return el.offsetParent !== null; }
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (!open.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* ---------- product gallery ---------- */
  // Frames sit side by side in a scroll-snap track: swiping scrolls it natively, and the
  // arrows, thumbnails and variant changes scroll it to a frame. The scroll position is
  // the single source of truth for which frame is current.
  function galleryFrames(gallery) {
    return gallery.querySelectorAll('[data-media-frame]');
  }

  function scrollGallery(gallery, index, instant) {
    var track = gallery.querySelector('[data-gallery-track]');
    var frames = galleryFrames(gallery);
    if (!track || !frames.length) return;
    index = Math.max(0, Math.min(frames.length - 1, index));
    track.scrollTo({ left: index * track.clientWidth, behavior: instant || reduceMotion ? 'auto' : 'smooth' });
  }

  function syncGallery(gallery) {
    var track = gallery.querySelector('[data-gallery-track]');
    var frames = galleryFrames(gallery);
    if (!track || !track.clientWidth || !frames.length) return;
    var index = Math.max(0, Math.min(frames.length - 1, Math.round(track.scrollLeft / track.clientWidth)));
    if (gallery._index === index) return;
    gallery._index = index;
    var currentId = frames[index].getAttribute('data-media-id');
    frames.forEach(function (frame, n) {
      if (n !== index) frame.querySelectorAll('video').forEach(function (video) { video.pause(); });
    });
    gallery.querySelectorAll('[data-thumb]').forEach(function (thumb) {
      thumb.setAttribute('aria-current', String(thumb.getAttribute('data-media-id') === currentId));
    });
    var prev = gallery.querySelector('[data-gallery-prev]');
    var next = gallery.querySelector('[data-gallery-next]');
    if (prev) prev.disabled = index === 0;
    if (next) next.disabled = index === frames.length - 1;
    var counter = gallery.querySelector('[data-gallery-current]');
    if (counter) counter.textContent = index + 1;
  }

  function showMedia(section, mediaId) {
    if (!section || !mediaId) return;
    var gallery = section.querySelector('[data-gallery]');
    if (!gallery) return;
    var frames = Array.prototype.slice.call(galleryFrames(gallery));
    var index = frames.findIndex(function (frame) { return frame.getAttribute('data-media-id') === String(mediaId); });
    if (index > -1) scrollGallery(gallery, index);
  }

  function initGallery(gallery) {
    var track = gallery.querySelector('[data-gallery-track]');
    if (!track) return;
    gallery._index = null;
    track.addEventListener('scroll', debounce(function () { syncGallery(gallery); }, 60), { passive: true });
    // Keep the current frame in place when the width changes (rotation, resize).
    window.addEventListener('resize', debounce(function () {
      if (gallery._index !== null) scrollGallery(gallery, gallery._index, true);
    }, 150));
    // Open on the selected variant's image.
    var frames = Array.prototype.slice.call(galleryFrames(gallery));
    var start = Math.max(0, frames.findIndex(function (frame) { return frame.hasAttribute('data-active'); }));
    scrollGallery(gallery, start, true);
    syncGallery(gallery);
  }

  /* ---------- variant picker ---------- */
  function getVariants(section) {
    if (!section._variants) {
      var json = section.querySelector('[data-variant-json]');
      section._variants = json ? JSON.parse(json.textContent) : [];
    }
    return section._variants;
  }

  function setText(scope, selector, text) {
    var el = scope.querySelector(selector);
    if (el) el.textContent = text;
    return el;
  }

  function updateVariant(section, variant) {
    var s = section.dataset;
    var available = !!(variant && variant.available);
    section.dataset.available = String(available);

    var button = section.querySelector('[data-add-button]');
    if (button) button.disabled = !available;
    setText(section, '[data-add-label]', !variant ? s.strUnavailable : available ? s.strAdd : s.strSoldOut);
    setText(section, '[data-availability]', !variant ? s.strUnavailable : available ? s.strInStock : s.strSoldOut);
    var dot = section.querySelector('[data-availability-dot]');
    if (dot) {
      dot.classList.toggle('bg-accent', available);
      dot.classList.toggle('bg-muted', !available);
    }
    if (!variant) return;

    var idInput = section.querySelector('[data-variant-id]');
    if (idInput) idInput.value = variant.id;

    setText(section, '[data-price]', variant.price);
    var compare = setText(section, '[data-compare]', variant.compare || '');
    if (compare) compare.hidden = !variant.compare;
    var save = setText(section, '[data-save]', variant.save ? s.strSave.replace('[percent]', variant.save) : '');
    if (save) save.hidden = !variant.save;

    var skuRow = section.querySelector('[data-sku-row]');
    if (skuRow) {
      skuRow.classList.toggle('hidden', !variant.sku);
      setText(section, '[data-sku]', variant.sku || '');
    }

    if (variant.media) showMedia(section, variant.media);

    var url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState(window.history.state, '', url.toString());
  }

  /* ---------- price filter slider ---------- */
  function paintPriceRange(wrapper) {
    var low = wrapper.querySelector('[data-range-min]');
    var high = wrapper.querySelector('[data-range-max]');
    var max = parseFloat(high.max) || 1;
    var fill = wrapper.querySelector('[data-range-fill]');
    fill.style.left = (parseFloat(low.value) / max) * 100 + '%';
    fill.style.right = 100 - (parseFloat(high.value) / max) * 100 + '%';
    // When the thumbs meet at the top end, keep the lower one grabbable.
    low.style.zIndex = parseFloat(low.value) > max / 2 ? '2' : '';
  }

  // Slider moved: keep the thumbs from crossing and mirror the values into the From / To boxes.
  // An end left at its limit is no filter at all, so its box stays empty (the placeholder shows the limit).
  function syncPriceInputs(wrapper, moved) {
    var low = wrapper.querySelector('[data-range-min]');
    var high = wrapper.querySelector('[data-range-max]');
    var lo = parseFloat(low.value);
    var hi = parseFloat(high.value);
    if (lo > hi) {
      if (moved === low) low.value = lo = hi;
      else high.value = hi = lo;
    }
    wrapper.querySelector('[data-price-min]').value = lo > 0 ? lo : '';
    wrapper.querySelector('[data-price-max]').value = hi < parseFloat(high.max) ? hi : '';
    paintPriceRange(wrapper);
  }

  document.addEventListener('input', function (event) {
    var wrapper = closest(event.target, '[data-price-range]');
    if (!wrapper) return;
    if (event.target.matches('[data-range-min], [data-range-max]')) {
      syncPriceInputs(wrapper, event.target);
    } else if (event.target.matches('[data-price-min], [data-price-max]')) {
      // Typed a price: move the matching thumb (the range input clamps it to its bounds).
      var isMin = event.target.hasAttribute('data-price-min');
      var thumb = wrapper.querySelector(isMin ? '[data-range-min]' : '[data-range-max]');
      thumb.value = event.target.value === '' ? (isMin ? thumb.min : thumb.max) : event.target.value;
      paintPriceRange(wrapper);
    }
  });

  document.addEventListener('change', function (event) {
    // Slider released: apply the filter. The short wait lets arrow-key presses add up into one reload.
    var thumb = closest(event.target, '[data-range-min], [data-range-max]');
    if (thumb && thumb.form) {
      var priceForm = thumb.form;
      clearTimeout(priceForm._priceTimer);
      priceForm._priceTimer = setTimeout(function () { priceForm.submit(); }, 400);
      return;
    }

    var autoSubmit = closest(event.target, '[data-auto-submit]');
    if (autoSubmit && autoSubmit.form) {
      autoSubmit.form.submit();
      return;
    }

    var country = closest(event.target, '[data-country-select]');
    if (country) {
      var province = country.form && country.form.querySelector('[data-province-select]');
      if (province) province.removeAttribute('data-default');
      syncProvinces(country);
      return;
    }

    var qtyInput = closest(event.target, '[data-qty-input]');
    if (qtyInput) {
      qtyInput.value = Math.max(1, parseInt(qtyInput.value, 10) || 1);
      return;
    }

    var input = closest(event.target, '[data-option-input]');
    if (!input) return;
    var section = closest(input, '[data-product-section]');
    if (!section) return;

    var selected = Array.prototype.map.call(section.querySelectorAll('[data-option-group]'), function (group) {
      var checked = group.querySelector('[data-option-input]:checked');
      if (checked) setText(group, '[data-selected-value]', checked.value);
      return checked ? checked.value : null;
    });

    updateVariant(section, getVariants(section).find(function (v) {
      return v.options.every(function (option, i) { return option === selected[i]; });
    }));
  });

  /* ---------- add to bag (AJAX; plain form post without JS) ---------- */
  document.addEventListener('submit', function (event) {
    var confirmForm = closest(event.target, '[data-confirm]');
    if (confirmForm && !window.confirm(confirmForm.getAttribute('data-confirm'))) {
      event.preventDefault();
      return;
    }

    if (!window.fetch) return;

    var quick = closest(event.target, '[data-quick-add]');
    if (quick) {
      event.preventDefault();
      var quickButton = quick.querySelector('button');
      quickButton.disabled = true;
      addToCart(quick)
        .then(function () {
          quickButton.textContent = quickButton.getAttribute('data-added');
          var drawer = getCartDrawer();
          if (drawer) openDrawer(drawer, quickButton);
        })
        .catch(function (error) { quickButton.textContent = error.shopMessage || quickButton.getAttribute('data-label'); })
        .then(function () {
          setTimeout(function () {
            quickButton.textContent = quickButton.getAttribute('data-label');
            quickButton.disabled = false;
          }, 2000);
        });
      return;
    }

    var form = closest(event.target, '[data-product-form]');
    if (!form) return;
    event.preventDefault();

    // Shopify turns 0 into "nothing added" and -1/"abc"/1.5 into 1, so send a clean whole number.
    var qtyField = form.querySelector('[data-qty-input]');
    if (qtyField) qtyField.value = Math.max(1, parseInt(qtyField.value, 10) || 1);

    var section = closest(form, '[data-product-section]') || form;
    var s = section.dataset;
    var button = form.querySelector('[data-add-button]');
    var label = form.querySelector('[data-add-label]');
    var status = form.querySelector('[data-cart-status]');
    var originalLabel = label ? label.textContent : '';

    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }
    if (label) label.textContent = s.strAdding || originalLabel;
    if (status) status.textContent = '';

    addToCart(form)
      .then(function () {
        var drawer = getCartDrawer();
        if (drawer) {
          openDrawer(drawer, button);
          return;
        }
        if (!status) return;
        status.textContent = (s.strAdded || '') + ' ';
        var link = document.createElement('a');
        link.href = root + 'cart';
        link.className = 'underline';
        link.textContent = s.strViewBag || 'View bag';
        status.appendChild(link);
      })
      .catch(function (error) {
        if (status) status.textContent = error.shopMessage || s.strError;
      })
      .then(function () {
        if (label) label.textContent = originalLabel;
        if (button) {
          button.removeAttribute('aria-busy');
          button.disabled = section.dataset.available === 'false';
        }
      });
  });

  /* ---------- accordions: one open at a time per group ---------- */
  // Native <details name> already does this in modern browsers; this covers older ones.
  document.addEventListener('toggle', function (event) {
    var details = event.target;
    if (!details.matches || !details.matches('details[data-accordion]') || !details.open) return;
    var group = details.getAttribute('data-accordion');
    document.querySelectorAll('details[data-accordion="' + group + '"][open]').forEach(function (other) {
      if (other !== details) other.open = false;
    });
  }, true);

  /* ---------- hero videos ---------- */
  function setVideoToggle(button, playing) {
    if (!button) return;
    button.querySelector('[data-icon-pause]').hidden = !playing;
    button.querySelector('[data-icon-play]').hidden = playing;
    button.setAttribute('aria-label', button.getAttribute(playing ? 'data-label-pause' : 'data-label-play'));
  }

  function playVideo(video) {
    video.muted = true;
    video.playsInline = true;
    var attempt = video.play();
    if (attempt && attempt.catch) attempt.catch(function () {});
  }

  function initSlideVideos(scope) {
    scope.querySelectorAll('[data-slide] video').forEach(function (video) {
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      if (reduceMotion) {
        video.removeAttribute('autoplay');
        video.pause();
        setVideoToggle(closest(video, '[data-slide]').querySelector('[data-video-toggle]'), false);
      }
    });
  }

  /* ---------- slideshow ---------- */
  function initSlideshow(el) {
    var track = el.querySelector('[data-slides]');
    var slides = el.querySelectorAll('[data-slide]');
    if (!track || slides.length < 2) return;

    var timer = null;
    var speed = (parseInt(el.getAttribute('data-speed'), 10) || 6) * 1000;
    el._index = 0;

    el._goTo = function (index) {
      el._index = (index + slides.length) % slides.length;
      track.style.transform = 'translateX(' + (-100 * el._index) + '%)';
      slides.forEach(function (slide, n) {
        var active = n === el._index;
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
        // Only the visible slide's video plays.
        var video = slide.querySelector('video');
        if (video) {
          if (active && !video._userPaused && !reduceMotion) playVideo(video);
          else video.pause();
        }
      });
      el.querySelectorAll('[data-slide-dot]').forEach(function (dot, n) {
        dot.setAttribute('aria-current', String(n === el._index));
      });
    };

    function stop() {
      if (timer) clearInterval(timer);
      timer = null;
    }

    el._restart = function () {
      stop();
      if (el.getAttribute('data-autoplay') === 'true' && !reduceMotion) {
        timer = setInterval(function () { el._goTo(el._index + 1); }, speed);
      }
    };

    el.addEventListener('mouseenter', stop);
    el.addEventListener('mouseleave', el._restart);
    el.addEventListener('focusin', stop);
    el.addEventListener('focusout', el._restart);

    var startX = null;
    el.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
    el.addEventListener('touchend', function (e) {
      if (startX === null) return;
      var dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 40) {
        el._goTo(el._index + (dx < 0 ? 1 : -1));
        el._restart();
      }
      startX = null;
    });

    el._goTo(0);
    el._restart();
  }

  // Theme editor: show the slide being edited.
  document.addEventListener('shopify:block:select', function (event) {
    var slide = closest(event.target, '[data-slide]');
    var slideshow = closest(event.target, '[data-slideshow]');
    if (!slide || !slideshow || !slideshow._goTo) return;
    slideshow._goTo(Array.prototype.indexOf.call(slideshow.querySelectorAll('[data-slide]'), slide));
  });

  /* ---------- scroll-snap carousel (testimonials) ---------- */
  function initCarousel(el) {
    var track = el.querySelector('[data-carousel-track]');
    var dots = el.querySelector('[data-carousel-dots]');
    var prev = el.querySelector('[data-carousel-prev]');
    var next = el.querySelector('[data-carousel-next]');
    if (!track || !dots) return;

    function pageCount() {
      // A hidden or zero-width track would divide by zero and loop forever.
      if (!track.clientWidth) return 1;
      var pages = Math.ceil(track.scrollWidth / track.clientWidth - 0.05);
      return Math.min(Math.max(1, pages || 1), 20);
    }

    function update() {
      if (!track.clientWidth) return;
      var current = Math.round(track.scrollLeft / track.clientWidth);
      dots.querySelectorAll('[data-carousel-dot]').forEach(function (dot, n) {
        dot.setAttribute('aria-current', String(n === current));
      });
      // Arrows fade out at each end (and both when everything fits).
      if (prev) prev.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
    }

    function render() {
      var count = pageCount();
      var template = dots.getAttribute('data-dot-label') || '[i]';
      dots.innerHTML = '';
      dots.hidden = count < 2;
      for (var i = 0; i < count; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'h-2.5 w-2.5 rounded-full bg-black/20 transition-colors aria-[current=true]:bg-accent';
        dot.setAttribute('data-carousel-dot', i);
        dot.setAttribute('aria-label', template.replace('[i]', i + 1));
        dots.appendChild(dot);
      }
      update();
    }

    track.addEventListener('scroll', debounce(update, 80));
    window.addEventListener('resize', debounce(render, 150));
    render();
  }

  /* ---------- product recommendations ---------- */
  function loadRecommendations(scope) {
    scope.querySelectorAll('[data-recommendations][data-url]').forEach(function (container) {
      fetch(container.getAttribute('data-url'))
        .then(function (response) { return response.text(); })
        .then(function (html) {
          var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recommendations]');
          if (fresh && fresh.innerHTML.trim()) {
            container.innerHTML = fresh.innerHTML;
            container.querySelectorAll('[data-carousel]').forEach(initCarousel);
          }
        })
        .catch(function () {});
    });
  }

  /* ---------- customer address forms: country -> province list ---------- */
  function syncProvinces(countrySelect) {
    var form = countrySelect.form;
    if (!form) return;
    var provinceSelect = form.querySelector('[data-province-select]');
    var wrapper = form.querySelector('[data-province-wrapper]');
    if (!provinceSelect) return;
    var option = countrySelect.options[countrySelect.selectedIndex];
    var provinces = [];
    try {
      provinces = JSON.parse((option && option.getAttribute('data-provinces')) || '[]');
    } catch (e) {}
    provinceSelect.innerHTML = '';
    provinces.forEach(function (pair) {
      var o = document.createElement('option');
      o.value = pair[0];
      o.textContent = pair[1];
      provinceSelect.appendChild(o);
    });
    if (wrapper) wrapper.hidden = provinces.length === 0;
    var preset = provinceSelect.getAttribute('data-default');
    if (preset) provinceSelect.value = preset;
  }

  function initAddressForms(scope) {
    scope.querySelectorAll('[data-country-select]').forEach(function (select) {
      var preset = select.getAttribute('data-default');
      if (preset) select.value = preset;
      syncProvinces(select);
    });
  }

  /* ---------- offer popup ---------- */
  // data-frequency: -1 = every page load, 0 = once per browser session, N = again after N days.
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  function popupStore(days) {
    try { return days === 0 ? window.sessionStorage : window.localStorage; } catch (e) { return null; }
  }

  // Another drawer or popup is showing (or mid-transition: un-hidden before .is-open is added).
  function otherDrawerOpen(popup) {
    return Array.prototype.some.call(document.querySelectorAll('[data-drawer]:not([hidden])'), function (el) {
      return el !== popup;
    });
  }

  function showPopupWhenFree(popup, onShow) {
    if (!document.body.contains(popup)) return; // section re-rendered or removed meanwhile
    // Don't stack on top of the cart or menu drawer; wait until it closes.
    if (otherDrawerOpen(popup)) {
      setTimeout(function () { showPopupWhenFree(popup, onShow); }, 2000);
      return;
    }
    openDrawer(popup);
    if (onShow) onShow();
  }

  function initOfferPopup(popup) {
    var form = popup.querySelector('form[id]');
    // Back from submitting the popup's own signup form: reopen it to show the result,
    // then drop the #form-id so a refresh doesn't reopen it again.
    if (form && window.location.hash === '#' + form.id) {
      openDrawer(popup);
      if (window.history && history.replaceState) {
        history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      return;
    }
    if (designMode) return; // opened by selecting the section instead
    if (popup.hasAttribute('data-mobile-off') && window.matchMedia('(max-width: 749px)').matches) return;

    var days = parseFloat(popup.getAttribute('data-frequency'));
    var key = 'mogra-popup-' + popup.getAttribute('data-popup-key');
    var store = days < 0 ? null : popupStore(days);
    if (store) {
      var seen = 0;
      try { seen = parseInt(store.getItem(key), 10) || 0; } catch (e) {}
      if (seen && (days === 0 || Date.now() - seen < days * 864e5)) return;
    }

    setTimeout(function () {
      // Count it as seen only once it has actually opened, not while it waits for the cart to close.
      showPopupWhenFree(popup, function () {
        if (!store) return;
        try { store.setItem(key, String(Date.now())); } catch (e) {}
      });
    }, (parseFloat(popup.getAttribute('data-delay')) || 0) * 1000);
  }

  // Theme editor: show the popup while its section is selected, and after each settings change.
  document.addEventListener('shopify:section:select', function (event) {
    var popup = event.target.querySelector('[data-offer-popup]');
    if (popup && popup.hidden) openDrawer(popup);
  });
  document.addEventListener('shopify:section:deselect', function (event) {
    var popup = event.target.querySelector('[data-offer-popup]');
    if (popup && !popup.hidden) closeDrawer(popup);
  });

  /* ---------- collection category tabs ---------- */
  // Centre the current category so it's visible without swiping. Sets scrollLeft
  // directly: scrollIntoView would also scroll the page.
  function initCategoryTabs(row) {
    var current = row.querySelector('[aria-current="page"]');
    if (!current || !row.clientWidth) return;
    var item = current.parentElement;
    row.scrollLeft = item.offsetLeft - (row.clientWidth - item.offsetWidth) / 2;
  }

  /* ---------- init ---------- */
  function init(scope) {
    initAddressForms(scope);
    initSlideVideos(scope);
    scope.querySelectorAll('[data-slideshow]').forEach(initSlideshow);
    scope.querySelectorAll('[data-carousel]').forEach(initCarousel);
    scope.querySelectorAll('[data-gallery]').forEach(initGallery);
    scope.querySelectorAll('[data-category-tabs]').forEach(initCategoryTabs);
    loadRecommendations(scope);
  }

  init(document);
  document.querySelectorAll('[data-offer-popup]').forEach(initOfferPopup);
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
    var popup = event.target.querySelector('[data-offer-popup]');
    if (popup && designMode) openDrawer(popup);
  });
})();
